import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import {
  renderSelectOptions,
  StyledTreasuryField,
  TreasuryPanelFrame,
  TreasuryTable,
} from "./TreasuryShared";

function normalizeErrorMessage(error, fallback) {
  const message = error?.message || fallback;
  if (error?.status === 409 || error?.data?.code === "DUPLICATE_NAME") {
    return message || "Ya existe un registro activo con ese nombre.";
  }
  return message;
}

export default function TreasuryCrudPanel({
  title,
  description,
  entityLabel,
  emptyMessage,
  typeLabel,
  typeField,
  typeOptions,
  list,
  create,
  update,
  remove,
  getTypeLabel,
  pluralLabel,
}) {
  const { empresaId } = useEmpresa();
  const licenseId = empresaId || "1";

  const initialForm = useMemo(
    () => ({
      name: "",
      [typeField]: typeOptions[0]?.value || "",
    }),
    [typeField, typeOptions]
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await list({ licenseId });
      setItems(Array.isArray(data) ? data : data?.items || []);
    } catch (error) {
      console.error(title, error);
      setItems([]);
      setToast({
        open: true,
        severity: "error",
        message: normalizeErrorMessage(
          error,
          `No se pudieron cargar ${pluralLabel || entityLabel.toLowerCase()}.`
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [entityLabel, licenseId, list, pluralLabel, title]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    setForm(initialForm);
    setFormErrors({});
  }, [initialForm]);

  const hasEditChanges = useMemo(() => {
    if (!editingItem) return false;
    return (
      String(editForm.name || "").trim() !== String(editingItem.name || "").trim() ||
      String(editForm[typeField] || "") !== String(editingItem[typeField] || "")
    );
  }, [editForm, editingItem, typeField]);

  const canCreate = String(form.name || "").trim().length > 0 && Boolean(form[typeField]) && !saving;
  const canEdit =
    String(editForm.name || "").trim().length > 0 &&
    Boolean(editForm[typeField]) &&
    hasEditChanges &&
    !saving;

  const resetForm = () => {
    setForm(initialForm);
    setFormErrors({});
  };

  const validateForm = (currentForm) => {
    const errors = {};
    if (!String(currentForm.name || "").trim()) errors.name = "Ingresá un nombre";
    if (!currentForm[typeField]) errors[typeField] = `Seleccioná ${typeLabel.toLowerCase()}`;
    return errors;
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setEditForm({
      name: item.name || "",
      [typeField]: item[typeField] || typeOptions[0]?.value || "",
    });
    setEditFormErrors({});
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditingItem(null);
    setEditForm(initialForm);
    setEditFormErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const payload = {
      name: String(form.name || "").trim(),
      [typeField]: form[typeField],
    };

    setSaving(true);
    try {
      await create(payload, { licenseId });
      setToast({
        open: true,
        severity: "success",
        message: `${entityLabel} creada correctamente.`,
      });
      resetForm();
      await loadItems();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: normalizeErrorMessage(
          error,
          `No se pudo crear ${entityLabel.toLowerCase()}.`
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingItem) return;

    const errors = validateForm(editForm);
    setEditFormErrors(errors);
    if (Object.keys(errors).length) return;

    const payload = {
      name: String(editForm.name || "").trim(),
      [typeField]: editForm[typeField],
    };

    setSaving(true);
    try {
      await update(editingItem.id, payload, { licenseId });
      setToast({
        open: true,
        severity: "success",
        message: `${entityLabel} actualizada correctamente.`,
      });
      setEditingItem(null);
      setEditForm(initialForm);
      setEditFormErrors({});
      await loadItems();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: normalizeErrorMessage(
          error,
          `No se pudo actualizar ${entityLabel.toLowerCase()}.`
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      `¿Querés eliminar ${entityLabel.toLowerCase()} "${item.name}"?`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await remove(item.id, { licenseId });
      setToast({
        open: true,
        severity: "success",
        message: `${entityLabel} eliminada correctamente.`,
      });
      if (String(editingItem?.id) === String(item.id)) {
        setEditingItem(null);
        setEditForm(initialForm);
        setEditFormErrors({});
      }
      await loadItems();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: normalizeErrorMessage(
          error,
          `No se pudo eliminar ${entityLabel.toLowerCase()}.`
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TreasuryPanelFrame
        title={title}
        description={description}
        onRefresh={loadItems}
        refreshing={loading}
        saving={saving}
      >
        <TreasuryTable
          columns={[
            { key: "name", label: "Nombre", strong: true },
            {
              key: typeField,
              label: typeLabel,
              render: (row) => getTypeLabel(row[typeField]),
            },
          ]}
          rows={items}
          loading={loading}
          emptyMessage={emptyMessage}
          selectedId={editingItem?.id}
          onEdit={openEditModal}
          onDelete={handleDelete}
          saving={saving}
        />

        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "24px",
            bgcolor: "neutral.50",
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900", mb: 2 }}>
            {`Agregar ${entityLabel.toLowerCase()}`}
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <StyledTreasuryField
                label="Nombre"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                required
                error={Boolean(formErrors.name)}
                helperText={formErrors.name}
                placeholder="Ej: Caja principal"
              />

              <StyledTreasuryField
                select
                label={typeLabel}
                name={typeField}
                value={form[typeField]}
                onChange={handleFormChange}
                required
                error={Boolean(formErrors[typeField])}
                helperText={formErrors[typeField]}
              >
                {renderSelectOptions(typeOptions)}
              </StyledTreasuryField>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Button
                onClick={resetForm}
                color="secondary"
                variant="outlined"
                disabled={saving}
                sx={{ borderRadius: "33px", px: 3, py: 1, textTransform: "none", fontWeight: 700 }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={saving || !canCreate}
                startIcon={<SaveIcon />}
                sx={{ borderRadius: "33px", px: 3, py: 1, textTransform: "none", fontWeight: 700 }}
              >
                {saving ? "Guardando..." : "Agregar"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </TreasuryPanelFrame>

      <Dialog
        open={Boolean(editingItem)}
        onClose={closeEditModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            bgcolor: "neutral.50",
          },
        }}
      >
        <Box component="form" onSubmit={handleEditSubmit}>
          <DialogTitle sx={{ fontWeight: 900, color: "primary.900", pb: 1 }}>
            {`Editar ${entityLabel.toLowerCase()}`}
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <StyledTreasuryField
                label="Nombre"
                name="name"
                value={editForm.name}
                onChange={handleEditFormChange}
                required
                error={Boolean(editFormErrors.name)}
                helperText={editFormErrors.name}
              />

              <StyledTreasuryField
                select
                label={typeLabel}
                name={typeField}
                value={editForm[typeField]}
                onChange={handleEditFormChange}
                required
                error={Boolean(editFormErrors[typeField])}
                helperText={editFormErrors[typeField]}
              >
                {renderSelectOptions(typeOptions)}
              </StyledTreasuryField>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={closeEditModal}
              color="secondary"
              variant="outlined"
              disabled={saving}
              sx={{ borderRadius: "33px", px: 3, textTransform: "none", fontWeight: 700 }}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={!canEdit}
              startIcon={<SaveIcon />}
              sx={{ borderRadius: "33px", px: 3, textTransform: "none", fontWeight: 700 }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
